import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import {
  Shield,
  Activity,
  Network,
  Search,
  FileText,
  Lock,
  Globe,
  Cpu,
  ArrowRight,
  CheckCircle,
  BarChart3,
  Users,
  AlertTriangle,
  Zap,
  Eye,
  Database,
  Bitcoin,
  TrendingUp,
  ChevronRight,
  Star,
  Volume2,
  Maximize,
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const features = [
  {
    icon: Shield,
    title: 'Advanced KYC/CDD',
    description: 'Comprehensive customer due diligence with real-time identity verification and risk profiling.',
    color: 'from-cyan-500 to-blue-500',
    glow: 'shadow-cyan-500/30'
  },
  {
    icon: Activity,
    title: 'AI Transaction Monitoring',
    description: 'ML-driven anomaly detection identifies suspicious patterns in real-time across all channels.',
    color: 'from-purple-500 to-pink-500',
    glow: 'shadow-purple-500/30'
  },
  {
    icon: Network,
    title: 'Network Analytics',
    description: 'Graph-based relationship mapping uncovers hidden connections and terrorist cells.',
    color: 'from-orange-500 to-red-500',
    glow: 'shadow-orange-500/30'
  },
  {
    icon: Bitcoin,
    title: 'Crypto Intelligence',
    description: 'Blockchain analytics and wallet tracing for cryptocurrency-enabled crimes.',
    color: 'from-yellow-500 to-orange-500',
    glow: 'shadow-yellow-500/30'
  },
  {
    icon: Search,
    title: 'Watchlist Screening',
    description: 'Real-time screening against UN, EU, OFAC sanctions and domestic terrorism lists.',
    color: 'from-green-500 to-emerald-500',
    glow: 'shadow-green-500/30'
  },
  {
    icon: FileText,
    title: 'Automated SAR/STR',
    description: 'AI-powered suspicious activity report generation for regulatory compliance.',
    color: 'from-blue-500 to-indigo-500',
    glow: 'shadow-blue-500/30'
  },
];

const stats = [
  { value: '99.9%', label: 'Threat Detection', icon: Eye },
  { value: '< 50ms', label: 'Response Time', icon: Zap },
  { value: '10M+', label: 'Transactions/Day', icon: TrendingUp },
  { value: '24/7', label: 'Monitoring', icon: Activity },
];

const capabilities = [
  'Multi-agency intelligence fusion',
  'Trade-based money laundering detection',
  'PEP and adverse media screening',
  'Entity resolution and linking',
  'Evidence-grade case management',
  'FATF & GIABA compliance',
];

export default function LandingPage() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="min-h-screen bg-[#030711] overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:50px_50px]" />
        
        {/* Gradient orbs */}
        <div 
          className="absolute w-[600px] h-[600px] rounded-full opacity-30 blur-[100px] transition-all duration-1000"
          style={{
            background: 'radial-gradient(circle, rgba(0, 240, 255, 0.3) 0%, transparent 70%)',
            left: mousePosition.x - 300,
            top: mousePosition.y - 300,
          }}
        />
        <div className="absolute top-0 left-1/4 w-[800px] h-[800px] rounded-full bg-gradient-to-r from-purple-500/10 to-pink-500/10 blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] rounded-full bg-gradient-to-r from-cyan-500/10 to-blue-500/10 blur-[100px]" />
        <div className="absolute top-1/2 right-0 w-[400px] h-[400px] rounded-full bg-gradient-to-r from-orange-500/10 to-red-500/10 blur-[80px]" />
      </div>

      {/* Navigation */}
      <nav className="relative z-10 border-b border-white/5 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-lg text-white tracking-tight">AEGIS</h1>
                <p className="text-[10px] text-cyan-400 font-mono tracking-widest">AML/CTF INTELLIGENCE</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-gray-300 hover:text-white" data-testid="nav-login-btn">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-lg shadow-cyan-500/30" data-testid="nav-register-btn">
                  Get Started
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 pt-20 pb-32">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge */}
            <motion.div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 mb-8"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
              <span className="text-sm text-cyan-400 font-medium">AEGIS AML/CTF Intelligence</span>
            </motion.div>

            {/* Main Heading */}
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="text-white">Detect. Disrupt.</span>
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent">
                Dismantle.
              </span>
            </h1>

            {/* Subheading */}
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
              AEGIS - The most advanced financial intelligence platform that fuses financial, trade, and digital intelligence 
              into a single sovereign system for evidence-grade investigations.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 hover:from-cyan-400 hover:via-blue-400 hover:to-purple-400 text-white px-8 py-6 text-lg shadow-2xl shadow-cyan-500/25 transition-all duration-300 hover:scale-105"
                  data-testid="hero-get-started-btn"
                >
                  Access Platform
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-gray-700 text-gray-300 hover:bg-white/5 px-8 py-6 text-lg"
                data-testid="hero-demo-btn"
              >
                <Eye className="w-5 h-5 mr-2" />
                Request Demo
              </Button>
            </div>
          </motion.div>

          {/* Floating Cards Animation */}
          <motion.div 
            className="mt-20 relative"
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            <div className="relative max-w-5xl mx-auto">
              {/* Main Dashboard Preview */}
              <div className="rounded-2xl bg-gradient-to-b from-gray-900/80 to-gray-950/80 backdrop-blur-xl border border-white/10 p-6 shadow-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="ml-4 text-xs text-gray-500 font-mono">Intelligence Dashboard</span>
                </div>
                
                {/* Mock Dashboard Content */}
                <div className="grid grid-cols-4 gap-4 mb-4">
                  {[
                    { label: 'Entities Monitored', value: '24,856', color: 'cyan', icon: Users },
                    { label: 'Active Alerts', value: '147', color: 'red', icon: AlertTriangle },
                    { label: 'Open Cases', value: '38', color: 'orange', icon: FileText },
                    { label: 'Risk Score', value: '72%', color: 'purple', icon: BarChart3 },
                  ].map((stat, i) => (
                    <motion.div
                      key={i}
                      className={`p-4 rounded-xl bg-gradient-to-br from-${stat.color}-500/10 to-transparent border border-${stat.color}-500/20`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 + i * 0.1 }}
                    >
                      <stat.icon className={`w-5 h-5 text-${stat.color}-400 mb-2`} />
                      <p className="text-2xl font-bold text-white">{stat.value}</p>
                      <p className="text-xs text-gray-500">{stat.label}</p>
                    </motion.div>
                  ))}
                </div>

                {/* Mock Chart Area */}
                <div className="h-40 rounded-lg bg-gradient-to-r from-cyan-500/5 via-purple-500/5 to-pink-500/5 border border-white/5 flex items-end justify-around p-4">
                  {[40, 65, 45, 80, 55, 70, 90, 60, 75, 85, 50, 95].map((h, i) => (
                    <motion.div
                      key={i}
                      className="w-6 rounded-t bg-gradient-to-t from-cyan-500 to-blue-500"
                      initial={{ height: 0 }}
                      animate={{ height: `${h}%` }}
                      transition={{ delay: 0.8 + i * 0.05, duration: 0.5 }}
                    />
                  ))}
                </div>
              </div>

              {/* Floating Elements */}
              <motion.div 
                className="absolute -top-4 -right-4 p-4 rounded-xl bg-gradient-to-br from-red-500/20 to-orange-500/20 backdrop-blur-xl border border-red-500/30 shadow-xl"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <AlertTriangle className="w-6 h-6 text-red-400 mb-2" />
                <p className="text-xs text-white font-medium">High Risk Alert</p>
                <p className="text-[10px] text-gray-400">Suspicious pattern detected</p>
              </motion.div>

              <motion.div 
                className="absolute -bottom-4 -left-4 p-4 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur-xl border border-green-500/30 shadow-xl"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 3.5, repeat: Infinity }}
              >
                <CheckCircle className="w-6 h-6 text-green-400 mb-2" />
                <p className="text-xs text-white font-medium">Case Resolved</p>
                <p className="text-[10px] text-gray-400">SAR-2024-0847</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative z-10 py-16 border-y border-white/5 bg-gradient-to-r from-cyan-500/5 via-transparent to-purple-500/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <stat.icon className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                <p className="text-4xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent mb-1">
                  {stat.value}
                </p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              Enterprise-Grade Intelligence
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              A comprehensive suite of tools designed for national security agencies and financial institutions.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {features.map((feature, i) => (
              <motion.div
                key={i}
                className={`group p-6 rounded-2xl bg-gradient-to-br from-gray-900/50 to-gray-950/50 backdrop-blur-sm border border-white/5 hover:border-white/20 transition-all duration-300 hover:shadow-xl ${feature.glow}`}
                variants={fadeInUp}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 shadow-lg`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="relative z-10 py-24 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Built for{' '}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  National Security
                </span>
              </h2>
              <p className="text-lg text-gray-400 mb-8">
                Designed to meet the stringent requirements of government security agencies, 
                with full FATF compliance and sovereign data control.
              </p>
              <div className="space-y-4">
                {capabilities.map((cap, i) => (
                  <motion.div
                    key={i}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-300">{cap}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-3xl blur-3xl" />
              <div className="relative rounded-2xl bg-gradient-to-br from-gray-900 to-gray-950 border border-white/10 p-8">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Globe, label: 'Global Coverage', value: '195+ Countries' },
                    { icon: Database, label: 'Data Sources', value: '50+ Integrations' },
                    { icon: Lock, label: 'Security', value: 'AES-256 Encryption' },
                    { icon: Cpu, label: 'AI Models', value: 'GPT-5.2 Powered' },
                  ].map((item, i) => (
                    <motion.div
                      key={i}
                      className="p-4 rounded-xl bg-white/5 border border-white/5"
                      whileHover={{ scale: 1.05 }}
                    >
                      <item.icon className="w-8 h-8 text-cyan-400 mb-3" />
                      <p className="text-white font-semibold">{item.value}</p>
                      <p className="text-xs text-gray-500">{item.label}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Video Demo Section */}
      <section className="relative z-10 py-24">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              See AEGIS in Action
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Watch how leading agencies use AEGIS to detect and prevent financial crime
            </p>
          </motion.div>
          
          <motion.div
            className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-gray-900 to-gray-950 border border-white/10 aspect-video"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            {/* Video Placeholder with animated gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-pink-500/10" />
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.button
                className="w-24 h-24 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center shadow-2xl shadow-cyan-500/30"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                data-testid="play-demo-btn"
              >
                <svg className="w-10 h-10 text-white ml-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </motion.button>
            </div>
            
            {/* Mock video thumbnail elements */}
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="px-3 py-1 rounded bg-black/50 backdrop-blur text-white text-sm">
                  Product Demo
                </div>
                <div className="text-gray-400 text-sm">3:45</div>
              </div>
              <div className="flex gap-2">
                <div className="w-8 h-8 rounded bg-black/50 backdrop-blur flex items-center justify-center">
                  <Volume2 className="w-4 h-4 text-white" />
                </div>
                <div className="w-8 h-8 rounded bg-black/50 backdrop-blur flex items-center justify-center">
                  <Maximize className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative z-10 py-24 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              Trusted by Leading Agencies
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Hear from security professionals who have transformed their AML/CTF operations
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                quote: "AEGIS has transformed our ability to detect complex money laundering networks. The AI-powered analytics cut our investigation time by 60%.",
                author: "Director of Financial Intelligence",
                org: "Central Bank of Nigeria",
                avatar: "CB"
              },
              {
                quote: "The network visualization feature helped us uncover a terrorist financing ring that had evaded detection for years. Game-changing technology.",
                author: "Head of Counter-Terrorism Finance",
                org: "Ministry of Interior",
                avatar: "MI"
              },
              {
                quote: "Finally, a platform that fuses all our intelligence sources into one coherent view. AEGIS is the gold standard for AML/CTF compliance.",
                author: "Chief Compliance Officer",
                org: "Pan-African Bank",
                avatar: "PA"
              }
            ].map((testimonial, i) => (
              <motion.div
                key={i}
                className="p-6 rounded-2xl bg-gradient-to-br from-gray-900/50 to-gray-950/50 backdrop-blur-sm border border-white/5"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">{testimonial.author}</p>
                    <p className="text-gray-500 text-xs">{testimonial.org}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Trust Logos */}
          <motion.div
            className="mt-16 pt-16 border-t border-white/5"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="text-center text-gray-500 text-sm mb-8">TRUSTED BY GOVERNMENT AGENCIES AND FINANCIAL INSTITUTIONS</p>
            <div className="flex flex-wrap items-center justify-center gap-12 opacity-50">
              {['FATF', 'GIABA', 'MENAFATF', 'APG', 'CFATF', 'EAG'].map((org, i) => (
                <div key={i} className="text-2xl font-bold text-gray-600 font-mono tracking-wider">
                  {org}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            className="p-12 rounded-3xl bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-purple-500/10 border border-white/10 backdrop-blur-xl"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Secure Your Nation?
            </h2>
            <p className="text-lg text-gray-400 mb-8 max-w-xl mx-auto">
              Join leading security agencies worldwide in the fight against financial crime 
              and terrorism financing.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-8 shadow-xl shadow-cyan-500/25"
                  data-testid="cta-get-started-btn"
                >
                  Get Started Now
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" size="lg" className="border-gray-700 text-gray-300 hover:bg-white/5 px-8">
                  Sign In
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <span className="text-gray-400 text-sm">
                Aegis Financial Intelligence
              </span>
            </div>
            <p className="text-gray-500 text-sm">
              © 2026 Aegis Financial Intelligence. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

